import { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import axios from 'axios';
import { Loading, TablePagination, Caret } from '../components/utility/Utility';
import { ReactComponent as ImagePlaceholderIcon } from '../assets/images/icons/image-placeholder.svg';
import { ReactComponent as EyeIcon } from '../assets/images/icons/eye.svg';
import '../assets/css/components/program.css';

const API = 'https://api.amplifyloyalty.com';

function statusPillClass(status) {
  const key = String(status || '').toUpperCase();
  if (key === 'ACTIVE') return 'product-catalog-status-pill--active';
  if (key === 'DRAFT') return 'product-catalog-status-pill--draft';
  if (key === 'ARCHIVED') return 'product-catalog-status-pill--archived';
  return 'product-catalog-status-pill--unknown';
}

function statusPillLabel(status) {
  const key = String(status || '').toUpperCase();
  if (key === 'ACTIVE') return 'Active';
  if (key === 'DRAFT') return 'Draft';
  if (key === 'ARCHIVED') return 'Archived';
  return status || '—';
}

export default function ProductAnalysisProducts() {
  const { getJwtToken, canWrite } = useContext(AuthContext);
  const [products, setProducts] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ title: 1 });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(false);
  const lastQuery = useRef('');
  const [searchTerm, setSearchTerm] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [syncError, setSyncError] = useState('');

  const paginate = async () => {
    setProducts(false);
    try {
      const token = await getJwtToken();
      const res = await axios.get(`${API}/app/product-analysis/products`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          sort: JSON.stringify(sort),
          query
        }
      });

      const info = { ...res.data };
      delete info.docs;
      setPagination(info);
      setProducts(res.data.docs || []);
    } catch (err) {
      console.log(err);
      setProducts([]);
    }
  };

  const search = (value) => {
    setTimeout(() => {
      const el = document.querySelector('.product-catalog-table__search');
      const currentValue = el ? el.value : value;
      if (currentValue === value && value !== lastQuery.current) {
        setProducts(false);
        lastQuery.current = value;
        setQuery(value);
        setPage(1);
      }
    }, 1500);
  };

  useEffect(() => {
    search(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    paginate();
  }, [query, sort, page]);

  const toggleTitleSort = () => {
    setSort({ title: sort.title === 1 ? -1 : 1 });
    setPage(1);
  };

  const runProductsSync = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Sync all active products from Shopify? This may take a few minutes.')) {
      return;
    }
    setSyncLoading(true);
    setSyncMessage('');
    setSyncError('');
    try {
      const token = await getJwtToken();
      const res = await axios.post(
        `${API}/app/product-analysis/products/sync/enqueue`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const pubCount = res?.data?.publications_synced;
      setSyncMessage(
        pubCount != null
          ? `Sales channels updated (${pubCount}). Product sync started — refresh in a few minutes.`
          : 'Sales channels updated. Product sync started — refresh in a few minutes.'
      );
    } catch (err) {
      setSyncError(
        err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Sync failed.'
      );
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div className="product-catalog">
      <section className="customers-table__container">
        <div className="customers-table">
          <div className="product-catalog-table__toolbar">
            <div className="customers-table__input-container product-catalog-table__search-wrap">
              <input
                type="text"
                className="customers-table__search product-catalog-table__search"
                placeholder="Search products by title, handle, SKU, or ID"
                onChange={(e) => setSearchTerm(e.target.value)}
                value={searchTerm}
              />
            </div>
            <button
              type="button"
              className="product-catalog-table__sync-btn"
              disabled={!canWrite || syncLoading}
              onClick={runProductsSync}
            >
              {syncLoading ? 'Starting sync…' : 'Sync from Shopify'}
            </button>
          </div>
          {(syncMessage || syncError) && (
            <div
              className={`product-catalog-table__sync-feedback${
                syncError ? ' product-catalog-table__sync-feedback--error' : ''
              }`}
              role="status"
            >
              {syncError || syncMessage}
            </div>
          )}
          <div className="customers-table__table-container">
            <div className="customers-table__header product-catalog-table__header">
              <div className="product-catalog-table__main-heading">
                <div
                  className="product-catalog-table__thumb-col product-catalog-table__thumb-col--header"
                  aria-label="View"
                >
                  <EyeIcon className="product-catalog-table__view-icon" aria-hidden="true" />
                </div>
                <div
                  className="customers-table__heading customers-table__sort-heading product-catalog-table__product-heading"
                  onClick={toggleTitleSort}
                >
                  <div className="customers-table__heading-text">Product</div>
                  {sort.title && (
                    <Caret className="customers-table__heading-caret" direction={sort.title} />
                  )}
                </div>
              </div>
              <div className="customers-table__metrics product-catalog-table__metrics">
                <div className="customers-table__heading product-catalog-table__metric-heading">
                  <div className="customers-table__heading-text">Type</div>
                </div>
                <div className="customers-table__heading product-catalog-table__metric-heading">
                  <div className="customers-table__heading-text">Status</div>
                </div>
              </div>
            </div>
            <div
              className={`customers-table__body${
                products === false || (Array.isArray(products) && products.length === 0)
                  ? ' customers-table__body--centered'
                  : ''
              }`}
            >
              {products === false && <Loading className="customers-table__loading" />}
              {Array.isArray(products) && products.length === 0 && (
                <div className="customers-table__empty-warning">No products found.</div>
              )}
              {Array.isArray(products) &&
                products.length > 0 &&
                products.map((product) => {
                  const pid = product.shopify_product_id;
                  const title = product.title || product.handle || pid;
                  return (
                    <div key={pid} className="customers-table__row product-catalog-table__row">
                      <Link
                        to={`/app/product-analysis/analytics/product/${pid}`}
                        state={{
                          from: '/app/product-analysis/products',
                          fromLabel: 'Products'
                        }}
                        className="product-catalog-table__main-link"
                      >
                        <div className="product-catalog-table__thumb-col">
                          {product.image_src ? (
                            <img
                              className="product-catalog-table__thumb"
                              src={product.image_src}
                              alt={product.image_alt || title}
                            />
                          ) : (
                            <div className="product-catalog-table__thumb-placeholder" aria-hidden="true">
                              <ImagePlaceholderIcon className="product-catalog-table__thumb-icon" />
                            </div>
                          )}
                        </div>
                        <span className="product-catalog-table__title">{title}</span>
                      </Link>
                      <div className="customers-table__metrics product-catalog-table__metrics">
                        <div className="customers-table__cell product-catalog-table__metric-cell">
                          {product.product_type || '—'}
                        </div>
                        <div className="customers-table__cell product-catalog-table__metric-cell">
                          <span
                            className={`product-catalog-status-pill ${statusPillClass(product.status)}`}
                          >
                            {statusPillLabel(product.status)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            {pagination && (
              <TablePagination
                options={pagination}
                docType="products"
                previous={() => setPage(pagination.prevPage)}
                next={() => setPage(pagination.nextPage)}
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
